import { DateTime } from 'luxon'
import { downloadFile, formatDateForDatabase } from '@utils'
import { Box, Button, Paper, Popover } from '@mui/material'
import { useState } from 'react'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import DatePicker from '@components/DatePicker'
import DownloadButton from '@components/DownloadButton'
import DownloadErrorDialog from '@components/DownloadErrorDialog'
import PeriodDatePicker from '@components/PeriodDatePicker'
import { STAFF_REPORTS_URL } from '@enums/api'

const DEFAULT_FROM_DATE = DateTime.now().startOf('month')
const DEFAULT_TO_DATE = DateTime.now()
const REPORT_API_ENDPOINT = `${STAFF_REPORTS_URL}/billable-hours`

const BillableHoursReportPage = () => {
    const [fromDate, setFromDate] = useState<DateTime | null | undefined>(
        DEFAULT_FROM_DATE
    )
    const [toDate, setToDate] = useState<DateTime | null | undefined>(
        DEFAULT_TO_DATE
    )
    const [downloading, setDownloading] = useState(false)
    const [downloadError, setDownloadError] = useState<string | null>(null)

    const [anchorElement, setAnchorElement] =
        useState<HTMLButtonElement | null>(null)

    const popoverOpen = Boolean(anchorElement)

    async function handleDownload() {
        setDownloading(true)
        const from = formatDateForDatabase(fromDate, 'start')
        const to = formatDateForDatabase(toDate, 'end')
        const fileName = `billable-hours_${from}-${to}.xlsx`

        // the backend is expecting the "to" to be exclusive, so add a day to
        // fake an inclusive bound
        const query = `?from=${from}&to=${to}`

        try {
            await downloadFile({
                acceptType:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                fileURL: REPORT_API_ENDPOINT + query,
                saveAsFileName: fileName,
            })
            setDownloading(false)
        } catch (error) {
            console.error(error)
            setDownloading(false)
            setDownloadError('Failed to download report')
        }
    }

    return (
        <DashboardLayout>
            <DownloadErrorDialog
                onClose={() => setDownloadError(null)}
                open={!!downloadError}
                title={downloadError}
            />
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 64,
                }}
            >
                <Paper
                    style={{
                        paddingInline: 32,
                        paddingBlock: 32,
                        textAlign: 'center',
                    }}
                >
                    <p>Select the date range for this Billable Hours report</p>
                    <div
                        style={{
                            display: 'flex',
                            marginTop: 32,
                        }}
                    >
                        <div style={{ marginRight: 16 }}>
                            <DatePicker
                                disabled={downloading}
                                label="From"
                                onChange={setFromDate}
                                value={fromDate}
                                maxDate={toDate}
                            />
                        </div>
                        <div>
                            <DatePicker
                                disabled={downloading}
                                label="To"
                                onChange={setToDate}
                                value={toDate}
                                minDate={fromDate}
                            />
                        </div>
                    </div>
                    <Box style={{ marginTop: 8 }}>
                        <Button
                            onClick={(e) => setAnchorElement(e.currentTarget)}
                        >
                            Quick Set
                        </Button>
                        <Popover
                            open={popoverOpen}
                            anchorEl={anchorElement}
                            onClose={() => setAnchorElement(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                        >
                            <Paper sx={{ padding: 1 }}>
                                <PeriodDatePicker
                                    fromDate={fromDate}
                                    setFromDate={setFromDate}
                                    toDate={toDate}
                                    setToDate={setToDate}
                                />
                            </Paper>
                        </Popover>
                    </Box>
                    <div style={{ marginTop: 32 }}>
                        <DownloadButton
                            downloading={downloading}
                            onClick={handleDownload}
                        >
                            Download report
                        </DownloadButton>
                        <p style={{ marginTop: 8 }}>
                            <small>
                                This will download a multi-sheet{' '}
                                <code>.xlsx</code> file
                            </small>
                        </p>
                    </div>
                </Paper>
            </div>
        </DashboardLayout>
    )
}

export default BillableHoursReportPage
